/**
 * Shared resolver for a user's saved quick links: filters by permissions/catalog,
 * expands `:clientId` tokens, and returns items ready to render in either
 * the inline sidebar section or the right-side drawer.
 */
import type { QuickLink } from '@/api/uiPreferences';
import type { FeatureFlags } from '@/config/feature-flags';
import {
  findQuickLinkPage,
  isQuickLinkPageAvailable,
  type QuickLinkPage,
} from '@/config/quickLinkPages';
import { expandPath } from '@/utils/expandPath';
import type { PermissionAction, PermissionFeature } from '@/utils/permissions';

export interface ResolvedQuickLink {
  link: QuickLink;
  catalog: QuickLinkPage;
  href: string;
}

export function resolveQuickLinks(
  links: QuickLink[] | undefined,
  hasPermission: (feature: PermissionFeature, action: PermissionAction) => boolean,
  clientId: string,
  flags?: FeatureFlags | null
): ResolvedQuickLink[] {
  if (!links || links.length === 0) return [];

  return [...links]
    .sort((a, b) => a.order - b.order)
    .map((link) => {
      const catalog = findQuickLinkPage(link.path, hasPermission);
      if (!catalog) return null;
      if (!hasPermission(catalog.requiredPermission.feature, catalog.requiredPermission.action)) {
        return null;
      }
      if (!isQuickLinkPageAvailable(catalog, flags)) return null;
      const href = expandPath(link.path, { clientId });
      if (!href) return null;
      return { link, catalog, href };
    })
    .filter((entry): entry is ResolvedQuickLink => Boolean(entry));
}
